import { AppCache, appValues, appURLS, GLOBAL_ANGULARJS_HTTP_TIMEOUT } from 'appRoot/app.constants';
import SwCacheService from 'app/services/swCache.service';
import BrowserInfoService from 'app/services/browserInfo.service';
import UtilsService from 'app/services/utils/utils.service';
import TelemetryService from 'app/services/telemetry/telemetry.service';
import ErrorTracking from 'app/services/errorTracking/errorTracking.service';
import GeneralSettingsService from 'external-services/general-settings-service/generalSettings.service';
import TrackingService from 'app/services/tracking/tracking.service';
import BookingNotificationsService from 'external-services/booking-notification-service/bookingNotifications.service';
import NetworkConnectionService from 'external-services/network-connection-service/index';
import ManagerService from 'external-services/manager-service/manager.service';
import AdminService from 'external-services/diary-admin-service/admin.service';
import VenueActions from 'app/actions/venue.actions';
import BlockOutsService from 'external-services/blockouts-service/blockouts.service';
import CustomerStateActions from 'app/actions/customerState.actions';
import LoginActions from 'app/actions/login.actions';
import PouchActions from 'app/actions/pouch.actions';
import ManagerActionsService from 'app/actions/manager.actions';
import { LoginRoutes, NgLocalStorageKey } from 'app/app.types';
import NetworkRequestsService from 'external-services/network-requests-service/index';
import SectionLookupService from 'external-services/section-lookup-service/sectionLookup.service';
import SectionGroupsService from 'external-services/section-groups-service/sectionGroups.service';
import LayoutsService from 'external-services/layouts-service/layouts.service';
import SimpleDialogActions from 'app/actions/simpleDialog.actions';
import StaffNameDialogActions from 'app/actions/staffNameDialog.actions';
import ScheduleLookupService from 'external-services/schedule-lookup-service/scheduleLookup.service';
import EventsLookupService from 'external-services/events-lookup-service';
import NetworkConnectionActionsService from 'app/actions/networkConnection.actions';
import CustomerLookupService from 'external-services/customer-lookup-service';
import LocalStorageService from 'external-services/local-storage-service/localStorage.service';
import PouchLoadService from 'external-services/pouch-load-service/index';
import store from 'appRoot/Store';
import { rootDisplayActions } from 'app/reducers/display/slice';
import OAuth2Service from 'external-services/oauth-login/OAuth2.service';
import { LocalStorageKey } from 'shared-types/index';
import HeaderMenuActions from 'app/actions/headerMenu.actions';
import StandbyListService from 'external-services/standby-list-service';
import TagsSerivce from 'external-services/tags-service';
import { NBIDiaryLocalStoragePrefix } from 'external-services/local-storage-service/types';
import RouteActions from 'app/actions/route.actions';

export function initServices(
  scheduleService: any,
  SnapService: any,
  $http: any,
  $rootScope: any,
  pouchService: any,
  AuthService: any,
  $filter: any,
  $state: any,
  $stateParams: any,
  $localStorage: any
) {
  LocalStorageService.init();
  LoginActions.setup($rootScope, $state, $stateParams, $localStorage);
  VenueActions.setup($rootScope);
  HeaderMenuActions.setup($rootScope);
  PouchActions.setup($rootScope);
  RouteActions.setup($state);
  pouchService.init();

  NetworkConnectionActionsService.setup($rootScope);
  NetworkConnectionService.setup(NetworkConnectionActionsService);
  NetworkRequestsService.init(() => AuthService.getToken());
  // @todo: pass false for storeLocally and update ManagerService.isManager everywhere to reference the redux store value instead
  ManagerService.setup(true, ManagerActionsService);

  AdminService.init(AuthService, LoginActions, VenueActions, appURLS.API, true);

  TelemetryService.setup($http, $rootScope);
  TelemetryService.init();
  scheduleService.init();
  BlockOutsService.setup(
    PouchActions,
    LoginActions,
    VenueActions,
    $filter,
    () => pouchService.getDBOnlineReducedAsFirstOption(),
    (...args) => TrackingService.trackEvent(...args),
    (...args) => TrackingService.trackError(...args)
  );
  BlockOutsService.init(
    () => AdminService.getVenueTimezoneId(),
    () => AdminService.getVenue().id,
    () => AdminService.getVenue().accountId
  );

  BookingNotificationsService.setup(pouchService);

  BookingNotificationsService.init();
  EventsLookupService.setup($rootScope);
  EventsLookupService.init(PouchActions, LoginActions, VenueActions);
  PouchLoadService.init(() => AdminService.getVenueTimezoneId());
  SectionLookupService.setup((...args) => TrackingService.trackEvent(...args));
  SectionGroupsService.setup((...args) => TrackingService.trackEvent(...args));
  SectionLookupService.init(LoginActions, VenueActions, PouchActions);
  GeneralSettingsService.init(LoginActions, VenueActions); // this also gets called in setup.controller `wizardSetups`
  // Removing Manju Changes done related to ChangeLog feature - NBI-4368-Removing-manju-changelog
  // ChangeLoggerService.setup(pouchService);

  UtilsService.adjustViewPortSize();
  SnapService.initMultitextPlugin();
  TrackingService.setup($rootScope, appValues);
  CustomerStateActions.setup($rootScope);
  LayoutsService.setup();
  LayoutsService.init(LoginActions, VenueActions, PouchActions);
  SimpleDialogActions.setup($rootScope);
  StaffNameDialogActions.setup($rootScope);
  ScheduleLookupService.setup(scheduleService, null, (...args) => TelemetryService.track(...args));
  CustomerLookupService.setup(AuthService);

  // Note: StandbyListService.setup is for BookingCreator. LegacyStandbyListService.setup is called in setup.controller.js
  StandbyListService.setup(
    appURLS.API,
    // getVenue must be fetched again in case they change venue within Diary
    () => AdminService.getVenue().accountId,
    () => AdminService.getVenue().id
  );

  TagsSerivce.init({
    TrackingService,
    PouchActions,
    VenueActions,
    getVenueId: () => AdminService.getVenue().id,
    getIsManager: () => ManagerService.isManager,
  });
}
export function initOAuth2(AuthService: any) {
  OAuth2Service.init(
    window.config.oauth2,
    (key: string) => LocalStorageService.get(key),
    (key: string, value: string) => LocalStorageService.set(key, value),
    (key: string) => LocalStorageService.remove(key),
    NBIDiaryLocalStoragePrefix
  );
  // Initialize visibility change handler for PC wake-up scenarios
  OAuth2Service.initVisibilityChangeHandler((newToken) =>
    AuthService.updateToken(newToken)
  );
  if (window.config.enableOAuth2) {
    // check whether already login by oauth2
    if (OAuth2Service.getOAuth2RefreshToken() && OAuth2Service.getOAuth2IdToken()) {
      OAuth2Service.setIsEnableOAuth(true);
      OAuth2Service.checkForUserAuth().then((isActive: boolean) => {
        if (isActive) {
          const accessToken = OAuth2Service.getOAuth2AccessToken();
          AuthService.updateToken(accessToken);
          OAuth2Service.periodicalCheckForTokenValidity(accessToken, (newToken) =>
            AuthService.updateToken(newToken)
          );
        }
      });
    }
  }
}
export function stateConfig(
  $rootScope: any,
  $state: any,
  pouchService: any,
  AuthService: any,
  $location: any
) {
  const browserInfo = BrowserInfoService.getBrowserInfo();
  let browserSupported = false;
  const browserName = browserInfo.name.toLowerCase();
  if (browserName === 'chrome' || browserName === 'safari' || browserName === 'netscape') {
    browserSupported = true;
  }

  if (navigator.maxTouchPoints) {
    window.addEventListener('contextmenu', function (e) {
      e.preventDefault();
    });
  }
  // check states
  $rootScope.$on('$stateChangeStart', (evt: any, nextState: any) => {
    store.dispatch(rootDisplayActions.setRouteStatus(nextState.name));
    if (nextState.name === 'browser-warning') {
      if (!browserSupported) {
        return true;
      } else {
        evt.preventDefault();
        $state.transitionTo(LoginRoutes.login);
        return false;
      }
    }
    if (!browserSupported && window.config.browserWarnings) {
      evt.preventDefault();
      $state.transitionTo('browser-warning');
      return false;
    }

    //TODO Remove
    // if (nextState.test) {
    //   return true;
    // }

    if (nextState.managerOnly && !ManagerService.isManager) {
      // this route is only allowed for managers
      evt.preventDefault();
      return false;
    }
    if (nextState.name === LoginRoutes.adminLogin) {
      // do nothing let the route though
    } else if (nextState.name === LoginRoutes.supportLogin) {
      // do nothing let the route though
    } else if (nextState.name === LoginRoutes.oauth2) {
      // do nothing let the route though
    } else if (nextState.name === LoginRoutes.directLogin) {
      // do nothing let the route though
    } else if (nextState.name === LoginRoutes.oauth2DirectLogin) {
      // do nothing let the route though
    } else if (nextState.name === LoginRoutes.supportOAuth2Login) {
      // do nothing let the route though
    } else if (!AdminService.checkLogin() || nextState.name === LoginRoutes.login) {
      // check if the user is Authenticated
      if (nextState.name !== LoginRoutes.login) {
        evt.preventDefault();
        $state.transitionTo(LoginRoutes.login, {
          completeState: nextState.name,
        });
      }
      return false;
      // make sure the app has been set up
    } else if (nextState.name !== 'setup' && !pouchService.setup) {
      evt.preventDefault();
      $state.transitionTo('setup', {
        completeState: nextState.name,
      });

      return false;
    } else if (!pouchService.created && nextState.name !== 'wizard' && nextState.name !== 'setup') {
      evt.preventDefault();
      $state.transitionTo('wizard');
      return false;
    }
    return false;
  });
}

export function tracking() {
  TrackingService.init();
}

export function checkAppVersionAgainstCache($http: any, $rootScope: any): boolean | void {
  /**
   * Not sure why 'swCacheService' is sometimes undefined, but it happened on NBI-1776, so handling it.
   */
  if (!SwCacheService.doCache()) {
    return false;
  }

  $http
    .get(`/app-version.json?cache-buster=${Date.now()}`)
    .then((response: { data: { version: string } }) => {
      // console.log(response.data.version, appValues.VERSION)
      if (response.data.version !== appValues.VERSION) {
        console.log(
          'New version of app detected',
          'You are on "' +
            appValues.VERSION +
            '" and version "' +
            response.data.version +
            '" is available.'
        );
        SwCacheService.setAvailableVersion(response.data.version);
        $rootScope.$broadcast('new-version');
      }
    })
    .catch(() => {
      console.warn(
        '"/app-version.json" failed to load. If you are running offline this is expected behaviour.'
      );
    });
}

/**
 * Registers service worker for running app offline.
 * This feature is ignored when command line flag `--skipCache true` is used,
 * which is default for local development.
 */
export function cacheRegister($http: any, $rootScope: any, $interval: any) {
  if (!appValues.SKIP_CACHE && 'serviceWorker' in navigator) {
    SwCacheService.cacheRegister();

    // check for version updates every hour (or whatever `UpdateCacheFrequency` is set to)
    $interval(() => {
      checkAppVersionAgainstCache($http, $rootScope);
    }, AppCache.UpdateCacheFrequency);
  }
}

export function themeConfig($mdThemingProvider: any) {
  $mdThemingProvider.definePalette('bookitPrimary', {
    50: 'rgba(244, 224, 235,1)',
    100: 'rgba(227, 182, 207,1)',
    200: 'rgba(211, 137, 177,1)',
    300: 'rgba(169, 17, 100, 1)',
    400: 'rgba(179, 55, 122,1)',
    500: 'rgba(169, 17, 100, 1)',
    600: 'rgba(151, 20, 91,1)',
    700: 'rgba(135, 18, 81,1)',
    800: 'rgba(119, 15, 71,1)',
    900: 'rgba(87, 9, 51,1)',
    A100: 'rgba(169, 17, 100,1)',
    A200: 'rgba(169, 17, 100,1)',
    A400: 'rgba(169, 17, 100,1)',
    A700: 'rgba(169, 17, 100,1)',
    contrastDefaultColor: 'light',
  });

  $mdThemingProvider.definePalette('bookitAccent', {
    50: 'rgba(244, 224, 235,1)',
    100: 'rgba(227, 182, 207,1)',
    200: 'rgba(211, 137, 177,1)',
    300: 'rgba(192, 89, 145,1)',
    400: 'rgba(179, 55, 122,1)',
    500: 'rgba(169, 17, 100, 1)',
    600: 'rgba(151, 20, 91,1)',
    700: 'rgba(135, 18, 81,1)',
    800: 'rgba(119, 15, 71,1)',
    900: 'rgba(87, 9, 51,1)',
    A100: 'rgba(169 , 17, 100,1)',
    A200: 'rgba(169 , 17, 100,1)',
    A400: 'rgba(169 , 17, 100,1)',
    A700: 'rgba(169 , 17, 100,1)',
    contrastDefaultColor: 'light',
  });

  $mdThemingProvider.theme('default').primaryPalette('bookitPrimary').accentPalette('bookitAccent');

  $mdThemingProvider.theme('saved');
  $mdThemingProvider.theme('cancelled');
}

export function addErrorReporting($provide: any) {
  // Provide an interceptor for the error workflow.
  $provide.decorator(
    '$exceptionHandler',
    /** @ngInject */
    function ($delegate: any) {
      // Return the new $exceptionHandler implementation which will
      // report the error to our internal error handler before passing
      // it off to the original $exceptionHandler implementation (which
      // may, itself, be some other delegate provided by another part
      // of the application).
      return function exceptionHandlerProxy(error: any, cause: any) {
        ErrorTracking.report(error);
        $delegate(error, cause);
      };
    }
  );
}

/*
    // https://stackoverflow.com/questions/41063947/angular-1-6-0-possibly-unhandled-rejection-error/41993170
    .config(['$qProvider', function ($qProvider) {
        $qProvider.errorOnUnhandledRejections(false);
      }]);
  */

export function timeoutHttpIntercept() {
  return {
    request: function (config: any) {
      // adds a default timeout for all $http requests in app, unless they already have a manual timeout defined
      config.timeout = config.timeout || GLOBAL_ANGULARJS_HTTP_TIMEOUT;
      return config;
    },
  };
}

export function httpProvider($httpProvider: any) {
  //Http Intercpetor to check auth failures for xhr requests
  $httpProvider.interceptors.push('authHttpResponseInterceptor');
  // $httpProvider.interceptors.push('oauth2Interceptor'); //as we retry the request, this interceptor is not needed
  $httpProvider.interceptors.push('apiInterceptor');
  $httpProvider.interceptors.push('timeoutHttpIntercept');
}

// RFC4122 compliant GUID generator for JS that is compact and "good enough" for what we want
export function uuidv4() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0,
      v = c == 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * Based losely on uuidv4 above, but shorter and just for css class name prefixes.
 * First character must be a letter, so using letter "y" just for sake of it.
 */
export function uniqueCssClassPrefix() {
  return 'yxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : c;
    return v.toString(16);
  });
}

/**
 * Adds custom headers for API calls, so we can track them in Seq more easily
 */
export function apiInterceptor($q: any) {
  return {
    request: function (request: any) {
      if (request.url.indexOf(appURLS.API) === 0) {
        request.headers['X-NBI-CorrelationId'] = NetworkRequestsService.getCorrelationId();
        request.headers['X-NBI-Source'] = NetworkRequestsService.getNBISource();
        request.headers['X-NBI-Version'] = appValues.VERSION;
      }
      return request || $q.when(request);
    },
  };
}
function checkForApiSecurity(request: any) {
  const excludedUrls = ['/api/oauth', '/api/forgotpassword', '/api/password'];
  if (request.url.indexOf(appURLS.API) === 0 && !excludedUrls.includes(request.url)) {
    return true;
  }
  return false;
}
export function oauth2Interceptor($q: any, $rootScope: any, $injector: any) {
  return {
    request: async function (request: any) {
      console.log('catch request====>', request);
      if (
        window.config.enableOAuth2 &&
        OAuth2Service.getIsEnableOAuth() &&
        checkForApiSecurity(request)
      ) {
        const isValidToken = OAuth2Service.checkForTokenValidity(
          LocalStorageService.get(LocalStorageKey.accessToken)
        );
        if (!isValidToken) {
          try {
            console.log('token expired, getting new token====>');
            const accessToken = await OAuth2Service.refreshAccessToken();
            console.log('new token===>', accessToken);
            // due to circular dependency, hardcode in here
            const AuthService = $injector.get('AuthService');
            AuthService.updateToken(accessToken);
            OAuth2Service.periodicalCheckForTokenValidity(accessToken, (newToken) =>
              AuthService.updateToken(newToken)
            );
          } catch (e) {
            $rootScope.$broadcast('unauthorised');
          }
        }
      }
      return request || $q.when(request);
    },
  };
}
export function authHttpResponseInterceptor($q: any, $rootScope: any) {
  // does offline check because we don't want timeouts to cause authorisation failures to force logout
  return {
    response: function (response: any) {
      if (NetworkConnectionService.isOnline() && response.status === 401) {
        $rootScope.$broadcast('unauthorised');
      }
      return response || $q.when(response);
    },
    responseError: async function (rejection: any, $injector: any) {
      // get 401 error
      if (rejection.status === 401) {
        // OAuth2 part
        if (OAuth2Service.getIsEnableOAuth()) {
          const $http = $injector.get('$http');
          const originalRequest = rejection.config;
          // first failed, refresh token and retry
          if (!originalRequest._retry) {
            console.log('Intercepted 401 error, try refresh token=', rejection);
            console.log('error.config', rejection.config);
            originalRequest._retry = true;
            //401, refresh token directly(token may not expire, but treat it like expired)
            const accessToken = await OAuth2Service.refreshAccessToken();
            const AuthService = $injector.get('AuthService');
            AuthService.updateToken(accessToken);
            OAuth2Service.periodicalCheckForTokenValidity(accessToken, (newToken) =>
              AuthService.updateToken(newToken)
            );
            originalRequest.headers['Authorization'] = 'Bearer ' + accessToken;
            return $q.when($http(originalRequest));
          } else {
            // second failed, logout
            if (NetworkConnectionService.isOnline()) {
              $rootScope.$broadcast('unauthorised');
            }
          }
        } else {
          // normal login part
          if (NetworkConnectionService.isOnline()) {
            $rootScope.$broadcast('unauthorised');
          }
        }
      }
      return $q.reject(rejection);
    },
  };
}

export function angularJSConfig(
  $compileProvider: any,
  $httpProvider: any,
  $animateProvider: any,
  $locationProvider: any
) {
  $locationProvider.hashPrefix('');
  // sync http request together
  $httpProvider.useApplyAsync(true);
  // for performace turn off debug info
  $compileProvider.debugInfoEnabled(false);
  //http://www.bennadel.com/blog/2935-enable-animations-explicitly-for-a-performance-boost-in-angularjs.htm?utm_campaign=NG-Newsletter&utm_medium=email&utm_source=NG-Newsletter_119
  //// By default, the $animate service will check for animation styling
  // on every structural change. This requires a lot of animateFrame-based
  // DOM-inspection. However, we can tell $animate to only check for
  // animations on elements that have a specific class name RegExp pattern
  // present. In this case, we are requiring the "animated" class.
  // --
  // NOTE: I have personally seen a performance boost using this approach
  // on some complex page. The AngularJS documentation also says that
  // this can also be really beneficial for low-powered mobile devices,
  // but I don't do much mobile.
  $animateProvider.classNameFilter(/(\banimated\b|\bbackdrop\b|\bmd-bottom\b|\bmd-right\b)/);
}
